import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DailyQuestsPill — compact header chip showing today's completion ratio.
 * Pulses yellow when bundles are ready to open. Links to /daily.
 */
export default function DailyQuestsPill() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get(`${API}/daily-quests/state`);
        if (!cancelled) setState(data);
      } catch (e) { /* ignore */ }
    }
    load();
    // Refresh once a minute (cheap) — keeps pill accurate after actions
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAuthed]);

  // Refresh on demand when other components fire daily-quest hooks
  useEffect(() => {
    function onTick() {
      if (!isAuthed) return;
      axios.get(`${API}/daily-quests/state`).then(r => setState(r.data)).catch(() => {});
    }
    window.addEventListener("omni:daily-quest-tick", onTick);
    return () => window.removeEventListener("omni:daily-quest-tick", onTick);
  }, [isAuthed]);

  if (!isAuthed || !state) return null;
  const done = state.claimed_count || 0;
  const total = state.total_tasks || 0;
  const anyBundleReady =
    (state.tiers?.small?.unlocked && state.bundle_claimed === null) ||
    (state.tiers?.mid?.unlocked && (state.bundle_claimed === "small" || state.bundle_claimed === null)) ||
    (state.tiers?.big?.unlocked && state.bundle_claimed !== "big");

  return (
    <Link
      to="/daily"
      data-testid="daily-quests-pill"
      className={`flex items-center gap-1.5 px-2.5 py-1 border transition-all ${
        anyBundleReady
          ? "border-yellow-400 bg-yellow-400/10 animate-pulse hover:bg-yellow-400/20"
          : "border-white/15 bg-black/40 hover:border-yellow-400/50"
      }`}
      style={{ borderRadius: 999 }}
      title={anyBundleReady ? "A daily bundle is ready to open!" : "Daily Calendar"}
    >
      <span className="text-base leading-none">📅</span>
      <span className="text-[11px] font-bold tabular-nums text-white">
        <span className={anyBundleReady ? "text-yellow-300" : ""}>{done}</span>
        <span className="text-white/40">/{total}</span>
      </span>
    </Link>
  );
}
